/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import React, { FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import { useGetFloorsByWarehouseIdQuery } from 'entities/Floor';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import {
  useCanvasDimensions,
  Shape,
  ShapeType,
  getGridCellSize,
  ConstructorBackground,
  useFloorSchemaImage,
  useCanvasScale,
  BoxShapeLabel,
} from 'features/WarehouseMapConstructor';
import Konva from 'konva';
import { Ellipse, Group, Layer, Line, Rect, Stage, Text } from 'react-konva';
import { SizeCodeType } from 'entities/SizeCode';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { Box, useGetWarehouseBoxesQuery } from 'entities/Box';
import { getSelectedFilter } from 'features/BoxSizeFilter';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';

interface WarehouseMapProps {
  selectedWarehouseId: string | undefined;
  selectedBoxId: string | undefined;
  allowedSizeCodeId?: string;
  preferredFloorId?: string;
  containerClassName?: string;
  highlightOnlySelected?: boolean;
  centerOnSelected?: boolean;
  onSelectBox: (box: Box, sizeCodeId: string) => void;
}

type ColorType = 'free' | 'occupied' | 'selected' | 'different';

export const WarehouseMap: FC<WarehouseMapProps> = memo((props) => {
  const {
    selectedWarehouseId,
    selectedBoxId,
    allowedSizeCodeId,
    preferredFloorId,
    containerClassName,
    highlightOnlySelected,
    centerOnSelected,
    onSelectBox,
  } = props;

  const { t } = useAppTranslation('booking');

  const [selectedFloor, setSelectedFloor] = useState<Nullable<string>>(null);
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
  const hasCenteredRef = useRef(false);
  const lastSelectedBoxIdRef = useRef<Nullable<string>>(null);
  const { stageParentRef, canvasDimensions } = useCanvasDimensions();
  const { canvasPosition, canvasScale, onMouseWheel } = useCanvasScale();

  const { image, loadImageSrc } = useFloorSchemaImage();

  const { data: floors } = useGetFloorsByWarehouseIdQuery(selectedWarehouseId, { skip: !selectedWarehouseId });
  const { data: boxes } = useGetWarehouseBoxesQuery({ warehouseId: selectedWarehouseId }, { skip: !selectedWarehouseId });

  const selectedFilter = useAppSelector(getSelectedFilter);
  const gridCellSize = useAppSelector(getGridCellSize);

  const resolvedPreferredFloorId = useMemo(() => {
    if (preferredFloorId) return preferredFloorId;
    if (!selectedBoxId || !floors) return undefined;

    for (const floor of floors) {
      if (!floor.shapesConfig) continue;
      try {
        const parsedShapes: Shape[] = JSON.parse(floor.shapesConfig);
        const hasSelectedShape = parsedShapes.some((shape) => String(shape.id) === String(selectedBoxId));
        if (hasSelectedShape) {
          return floor.floorId;
        }
      } catch {
        // ignore invalid shapesConfig for floor detection
      }
    }

    return undefined;
  }, [floors, preferredFloorId, selectedBoxId]);

  const floorSchemaImage = floors?.find((floor) => floor.floorId === selectedFloor)?.floorSchemaBackgroundUrl;

  const selectFloor = (floorId: string): void => {
    setSelectedFloor(floorId);
  };

  const selectBox = (box: Box, sizeCodeId: string): void => {
    onSelectBox(box, sizeCodeId);
  };

  useEffect(() => {
    const selectedBoxChanged = selectedBoxId && lastSelectedBoxIdRef.current !== selectedBoxId;

    if (resolvedPreferredFloorId && (selectedFloor === null || selectedBoxChanged)) {
      selectFloor(resolvedPreferredFloorId);
      lastSelectedBoxIdRef.current = selectedBoxId || null;
      return;
    }

    if (floors && !selectedFloor) {
      selectFloor(floors[0].floorId);
    }
  }, [floors, resolvedPreferredFloorId, selectedBoxId, selectedFloor]);

  useEffect(() => {
    if (floorSchemaImage) {
      loadImageSrc(floorSchemaImage);
    }
  }, [floorSchemaImage, loadImageSrc]);

  const renderedFloor = useMemo(() => {
    return floors?.find((floor) => floor.floorId === selectedFloor);
  }, [floors, selectedFloor]);

  const shapes: Shape[] = useMemo(() => {
    return renderedFloor?.shapesConfig ? JSON.parse(renderedFloor.shapesConfig) : [];
  }, [renderedFloor?.shapesConfig]);

  // Center stage on selected box
  useEffect(() => {
    if (!centerOnSelected || !selectedBoxId || !shapes.length || !canvasDimensions.width || hasCenteredRef.current) {
      return;
    }

    const selectedShape = shapes.find((shape) => String(shape.id) === String(selectedBoxId));
    if (!selectedShape || !('x' in selectedShape) || !('width' in selectedShape)) {
      return;
    }

    const shapeWidth = selectedShape.width || 0;
    const shapeHeight = selectedShape.height || 0;
    const shapeCenterX = selectedShape.x + shapeWidth / 2;
    const shapeCenterY = selectedShape.y + shapeHeight / 2;

    const offsetX = canvasDimensions.width / 2 - shapeCenterX;
    const offsetY = canvasDimensions.height / 2 - shapeCenterY;

    setStagePosition({ x: offsetX, y: offsetY });
    hasCenteredRef.current = true;
  }, [centerOnSelected, selectedBoxId, shapes, canvasDimensions]);

  const selectedFill = '#DEE8FF';
  const selectedStroke = '#2058E7';
  const occupiedFill = '#FEE4E2';
  const occupiedStroke = '#EF4444';
  const freeFill = '#E2FEF1';
  const freeStroke = '#2BA865';
  const defaultFill = '#F3F6F9';
  const defaultStroke = '#788593';

  const getLegendClass = (type: ColorType): string => {
    const commonClass = 'w-4 h-4 mr-2.5 rounded-full border';

    const colorMap: Record<ColorType, string> = {
      free: 'border-[#2BA865] bg-[#E2FEF1]',
      occupied: 'border-[#EF4444] bg-[#FEE4E2]',
      selected: 'border-[#2058E7] bg-[#DEE8FF]',
      different: 'border-[#788593] bg-[#F3F6F9]',
    };

    return `${commonClass} ${colorMap[type]}`;
  };

  const rectangleLabels: React.ReactNode[] = [];

  return (
    <div className={classNames('flex flex-col h-full', containerClassName)}>
      <div className="bg-white p-7 rounded-lg flex flex-col h-full">
        <div className="flex items-center justify-center space-x-2 z-50">
          {floors?.map((floor) => (
            <div
              key={floor.floorId}
              className={classNames('py-1.5 px-3.5 rounded-lg text-primaryLight cursor-pointer', {
                'bg-accent text-white': selectedFloor === floor.floorId,
              })}
              onClick={() => {
                selectFloor(floor.floorId);
              }}
            >
              {getLocalizedString(floor.name)}
            </div>
          ))}
        </div>
        <div ref={stageParentRef} className="relative flex-1 min-h-0 touch-none">
          <Stage
            width={canvasDimensions.width}
            height={canvasDimensions.height || 500}
            onWheel={onMouseWheel}
            x={canvasPosition.x}
            y={canvasPosition.y}
            draggable
            scale={{ x: canvasScale, y: canvasScale }}
          >
            <Layer>
              <ConstructorBackground image={image} />
              {shapes.map((shape) => {
                const fontSize = 12;

                const targetBox = boxes?.find((box) => box.boxId === shape.id);
                const isOccupied = targetBox?.contractNumber || targetBox?.reservationNumber;
                const isMatchedSizeGroup = selectedFilter !== 'All' ? targetBox?.sizeCode.sizeGroup === selectedFilter : true;
                const isAllowedSizeCode = allowedSizeCodeId ? targetBox?.sizeCode.sizeCodeId === allowedSizeCodeId : true;
                const isSelected = selectedBoxId ? String(selectedBoxId) === String(shape.id) : false;

                const boxColor = (): { fill: string; stroke: string } => {
                  if (isSelected) {
                    return {
                      fill: selectedFill,
                      stroke: selectedStroke,
                    };
                  }

                  if (highlightOnlySelected) {
                    return {
                      fill: defaultFill,
                      stroke: defaultStroke,
                    };
                  }

                  if (isMatchedSizeGroup && isAllowedSizeCode) {
                    return {
                      fill: isOccupied ? occupiedFill : freeFill,
                      stroke: isOccupied ? occupiedStroke : freeStroke,
                    };
                  }

                  return {
                    fill: defaultFill,
                    stroke: defaultStroke,
                  };
                };

                const areaText = `${formatDecimal(roundNumber(targetBox?.sizeCode.square || 0))} ${
                  (targetBox?.sizeCode.sizeCodeType === SizeCodeType.CUBIC_BOX ? 'м³' : 'м²') || ''
                }`;

                switch (shape.type) {
                  case ShapeType.RECTANGLE: {
                    const scaleX = shape.scaleX || 1;
                    const scaleY = shape.scaleY || 1;
                    const visualWidth = Math.max(shape.width * scaleX, 1);
                    const visualHeight = Math.max(shape.height * scaleY, 1);
                    const labelLine = String(shape.label ?? '');
                    const labelByWidth = (visualWidth * 0.9) / (Math.max(labelLine.length, 1) * 0.55);
                    const areaByWidth = (visualWidth * 0.9) / (Math.max(areaText.length, 1) * 0.55);
                    const fittedLabel = Math.min(fontSize, labelByWidth, (visualHeight * 0.84) / 1.9);
                    const labelFontSize = Math.max(1, Math.round(fittedLabel * 10) / 10);
                    const areaFontSize = Math.max(
                      1,
                      Math.round(Math.min(labelFontSize * 0.65, areaByWidth, visualHeight * 0.38) * 10) / 10,
                    );
                    const lineGap = Math.max(1, labelFontSize * 0.1);
                    const textBlockHeight = labelFontSize + lineGap + areaFontSize;

                    rectangleLabels.push(
                      <Group
                        key={`box-label-${shape.id}`}
                        x={shape.x}
                        y={shape.y}
                        scaleX={shape.scaleX}
                        scaleY={shape.scaleY}
                        rotation={shape.rotation}
                        listening={false}
                      >
                        {shape.labelPosition || shape.labelScale !== undefined ? (
                          <BoxShapeLabel
                            label={shape.label}
                            areaText={areaText}
                            width={shape.width}
                            height={shape.height}
                            scaleX={shape.scaleX}
                            scaleY={shape.scaleY}
                            labelPosition={shape.labelPosition}
                            labelScale={shape.labelScale}
                            labelFontSize={shape.labelFontSize}
                          />
                        ) : (
                          <Group
                            x={shape.width / 2}
                            y={shape.height / 2}
                            scaleX={1 / scaleX}
                            scaleY={1 / scaleY}
                            clipX={-visualWidth / 2}
                            clipY={-visualHeight / 2}
                            clipWidth={visualWidth}
                            clipHeight={visualHeight}
                          >
                            <Text
                              text={labelLine}
                              width={visualWidth}
                              offsetX={visualWidth / 2}
                              align="center"
                              fontSize={labelFontSize}
                              y={-textBlockHeight / 2}
                              wrap="none"
                            />
                            <Text
                              text={areaText}
                              width={visualWidth}
                              offsetX={visualWidth / 2}
                              align="center"
                              fontSize={areaFontSize}
                              y={-textBlockHeight / 2 + labelFontSize + lineGap}
                              wrap="none"
                            />
                          </Group>
                        )}
                      </Group>,
                    );

                    return (
                      <Group
                        key={shape.id}
                        name="shape"
                        id={shape.id}
                        x={shape.x}
                        y={shape.y}
                        scaleX={shape.scaleX}
                        scaleY={shape.scaleY}
                        width={shape.width}
                        height={shape.height}
                        rotation={shape.rotation}
                        onClick={() => {
                          if (targetBox && !isOccupied && isAllowedSizeCode && !highlightOnlySelected) {
                            selectBox(targetBox, targetBox.sizeCode.sizeCodeId);
                          }
                        }}
                        onTap={() => {
                          if (targetBox && !isOccupied && isAllowedSizeCode && !highlightOnlySelected) {
                            selectBox(targetBox, targetBox.sizeCode.sizeCodeId);
                          }
                        }}
                      >
                        <Rect
                          id={shape.id}
                          width={shape.width}
                          height={shape.height}
                          fill={boxColor().fill}
                          stroke={boxColor().stroke}
                          strokeWidth={shape.strokeWidth}
                          cornerRadius={shape.cornerRadius}
                          strokeScaleEnabled={false}
                        />
                      </Group>
                    );
                  }

                  case ShapeType.LINE:
                    return (
                      <Line
                        key={shape.id}
                        id={shape.id}
                        name="shape"
                        fill={shape.fill}
                        stroke={shape.stroke}
                        strokeWidth={shape.strokeWidth}
                        x={0}
                        y={0}
                        points={shape.points}
                        scaleX={shape.scaleX}
                        scaleY={shape.scaleY}
                        rotation={shape.rotation}
                      />
                    );

                  case ShapeType.CIRCLE:
                    return (
                      <Group
                        key={shape.id}
                        name="shape"
                        id={shape.id}
                        x={shape.x}
                        y={shape.y}
                        scaleX={shape.scaleX}
                        scaleY={shape.scaleY}
                        width={shape.radiusX * 2}
                        height={shape.radiusY * 2}
                        rotation={shape.rotation}
                      >
                        <Ellipse
                          key={shape.id}
                          id={shape.id}
                          fill={shape.fill}
                          stroke={shape.stroke}
                          radiusX={shape.radiusX}
                          radiusY={shape.radiusY}
                          strokeWidth={shape.strokeWidth}
                          cornerRadius={shape.cornerRadius}
                          strokeScaleEnabled={false}
                          width={shape.radiusX * 2}
                          height={shape.radiusY * 2}
                        />
                        <Text
                          text={shape.label}
                          x={shape.radiusX / fontSize / 2}
                          y={shape.radiusY / fontSize / 2}
                          fontSize={fontSize}
                          offsetX={shape.label.length * (fontSize / 4)}
                          offsetY={shape.radiusY / fontSize}
                          align="center"
                          verticalAlign="middle"
                        />
                      </Group>
                    );

                  case ShapeType.TEXT:
                    return <Text key={shape.id} name="shape" {...shape} text={shape.label} strokeWidth={1} />;

                  case ShapeType.POLYGON:
                    return <Line key={shape.id} name="shape" {...shape} x={0} y={0} closed />;

                  default:
                    return undefined;
                }
              })}
              {rectangleLabels}
            </Layer>
          </Stage>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-white shadow-lg rounded-xl px-4 py-2 w-[90vw] max-w-[340px] grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <div className="flex items-center gap-2">
              <div className={`${getLegendClass('free')} w-4 h-4 shrink-0`} />
              <span>{t('Free boxes')}</span>
            </div>

            <div className="flex items-center gap-2">
              <div className={`${getLegendClass('occupied')} w-4 h-4 shrink-0`} />
              <span>{t('Occupied boxes')}</span>
            </div>

            <div className="flex items-center gap-2">
              <div className={`${getLegendClass('selected')} w-4 h-4 shrink-0`} />
              <span>{t('Selected boxes')}</span>
            </div>

            <div className="flex items-center gap-2">
              <div className={`${getLegendClass('different')} w-4 h-4 shrink-0`} />
              <span>{t('Different size box')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
